import { useId, type ReactNode, type Ref } from 'react';
import { useTranslation } from '@mypoz/i18n';
import { Icon, cn } from '@mypoz/ui';

import { selectClass } from '@/lib/styles.js';

/**
 * The search box, sort menu and filter chips shared by the two long pickers in the order flow —
 * Step 1's packages and Step 2's channels. Kept small and dumb: state and the filtering itself
 * live with each step (and the filtering in `@mypoz/shared`'s `order-pickers`).
 */

export function PickerSearch({
  label,
  placeholder,
  value,
  onChange,
  className,
  inputRef,
  onEscape,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  /** Sizing from the toolbar it sits in (MYP-131: a fixed width beside the chips on laptop). */
  className?: string;
  /** For a toolbar that moves focus into the box (phone order layout: the phone search button). */
  inputRef?: Ref<HTMLInputElement>;
  /** Escape in the box (phone order layout: closes the phone search, as Cancel does). */
  onEscape?: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();

  return (
    <div className={cn('relative min-w-0', className ?? 'flex-1')}>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <Icon
        name="search"
        size={20}
        className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-muted"
      />
      <input
        id={id}
        type="search"
        value={value}
        placeholder={placeholder}
        ref={inputRef}
        onKeyDown={
          onEscape
            ? (event) => {
                if (event.key === 'Escape') onEscape();
              }
            : undefined
        }
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          // pr-14 clears the clear button, as on the Packages filter bar — only while there is one
          // (phone order layout: on a phone the box shares its row, and the placeholder needs the room).
          'min-h-11 w-full rounded-lg bg-surface pl-11 text-sm text-ink ring-1 ring-line lg:min-h-10',
          value ? 'pr-14' : 'pr-3',
          'transition-colors placeholder:text-muted hover:bg-surface-alt',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
        )}
      />
      {value ? (
        <button
          type="button"
          aria-label={t('orders.picker.clearSearch')}
          onClick={() => onChange('')}
          className="absolute top-1/2 right-1.5 flex size-11 -translate-y-1/2 items-center justify-center rounded-md text-muted transition-colors hover:bg-line/60 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:size-9"
        >
          <Icon name="close" size={18} />
        </button>
      ) : null}
    </div>
  );
}

export function PickerSort<T extends string>({
  value,
  defaultValue,
  options,
  onChange,
}: {
  value: T;
  /** The list's own order ("Default order"); any other value is a sort in use, shown by a dot. */
  defaultValue: T;
  options: readonly { readonly value: T; readonly label: string }[];
  onChange: (value: T) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const sorted = value !== defaultValue;

  /*
    phone order layout: one native <select> at every size. From `sm` it shows as a labelled menu; on a phone
    it is laid, invisible, over a 44px sort icon, so tapping the icon opens the phone's own picker
    and the toolbar keeps one row. A dot on the icon says a sort other than the default is on.
  */
  return (
    <div className="flex shrink-0 items-center gap-2">
      <label htmlFor={id} className="sr-only text-sm whitespace-nowrap text-muted sm:not-sr-only">
        {t('orders.picker.sortLabel')}
      </label>
      <div
        className={cn(
          'relative size-11 rounded-full sm:size-auto sm:rounded-md',
          'has-[select:focus-visible]:outline-2 has-[select:focus-visible]:outline-offset-2 has-[select:focus-visible]:outline-brand sm:has-[select:focus-visible]:outline-0',
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-0 flex items-center justify-center rounded-full border sm:hidden',
            sorted ? 'border-brand bg-brand-soft text-brand' : 'border-line bg-surface text-ink',
          )}
        >
          <Icon name="swap_vert" size={20} />
          {sorted ? (
            <span className="absolute top-0.5 right-0.5 size-2.5 rounded-full border-2 border-surface bg-brand" />
          ) : null}
        </span>
        <select
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value as T)}
          className={selectClass(
            'absolute inset-0 size-full min-h-11 opacity-0 sm:static sm:h-auto sm:w-auto sm:opacity-100 lg:min-h-10',
          )}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {/* Hidden through a wrapper: the icon font's own `display` would beat `hidden`. */}
        <span className="pointer-events-none absolute top-1/2 right-3 hidden -translate-y-1/2 text-muted sm:block">
          <Icon name="expand_more" size={20} className="block" />
        </span>
      </div>
    </div>
  );
}

/** A row of toggle chips; exactly one is pressed. */
export function PickerChips<T extends string>({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string;
  value: T;
  options: readonly {
    readonly value: T;
    readonly label: string;
    /** Drawn instead of `label` on a phone (phone order layout); `label` stays the button's name. */
    readonly short?: ReactNode;
  }[];
  onChange: (value: T) => void;
  /** Layout from the toolbar it sits in (phone order layout: one scrolling line on a phone). */
  className?: string;
}) {
  return (
    <div role="group" aria-label={label} className={cn('flex flex-wrap gap-2', className)}>
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'min-h-11 shrink-0 rounded-full border px-4 text-sm whitespace-nowrap transition-colors lg:min-h-9',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
              active
                ? 'border-brand bg-brand-soft font-semibold text-brand'
                : 'border-line bg-surface text-ink hover:bg-surface-alt',
            )}
          >
            {option.short !== undefined ? (
              <>
                <span aria-hidden="true" className="inline-flex items-center gap-1 sm:hidden">
                  {option.short}
                </span>
                <span className="sr-only sm:not-sr-only">{option.label}</span>
              </>
            ) : (
              option.label
            )}
          </button>
        );
      })}
    </div>
  );
}

/** What a picker shows when its filters leave nothing: the reason, and the way back. */
export function PickerEmpty({ message, onReset }: { message: string; onReset: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center gap-3 px-5 py-10 text-center">
      <Icon name="search_off" size={28} className="text-muted" />
      <p className="text-sm text-ink">{message}</p>
      <button
        type="button"
        onClick={onReset}
        className="text-sm font-semibold text-brand transition-colors hover:text-brand-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        {t('orders.picker.reset')}
      </button>
    </div>
  );
}
